import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';

import type { ModuleType } from 'infrastructure/enums';
import type { IGetPatientReportsResponse } from 'components/organisms/provider-dashboard';

export interface IGetProviderDashboardPatientReportsFilters {
  startDate: string;
  endDate: string;
  clinicGuid?: string[];
  parentClinicGuid?: string[];
  providerGuid?: string;
  module: ModuleType;
}

const getUrl = (module: ModuleType) =>
  `core-2/provider-dashboard/patient-reports/${module}`;

export const getProviderDashboardPatientReportsQueryKey = (
  filters: IGetProviderDashboardPatientReportsFilters,
) => {
  const { module, ...otherFilter } = filters;
  return [getUrl(module), otherFilter] as const;
};

const queryFn = async (filters: IGetProviderDashboardPatientReportsFilters) => {
  const { module, ...otherFilter } = filters;
  const url = getUrl(module);

  const { data, error } = await dataService.getList<IGetPatientReportsResponse>(
    apiUrlGenerator(url, otherFilter),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useGetProviderDashboardPatientReports = (
  filters: IGetProviderDashboardPatientReportsFilters,
  enabled = true,
) => {
  const queryKey = getProviderDashboardPatientReportsQueryKey(filters);

  const { data, isLoading, isFetching } = useQuery<
    IGetPatientReportsResponse | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(filters),
    keepPreviousData: true,
    enabled,
  });

  return {
    patientReports: data,
    isLoading,
    isFetching,
  };
};
